import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaUniversity,
  FaUsers,
  FaCode,
  FaBrain,
  FaCloud,
  FaPaintBrush,
  FaPlus,
  FaArrowLeft,
  FaSearch,
  FaEye,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaUserGraduate,
  FaChartLine,
  FaShieldAlt,
  FaServer,
  FaSortAmountDown,
  FaFileExcel,
  FaFilePdf,
  FaCheckCircle
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import userMasterService from '../services/userMasterService.jsx';

const UserMaster = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [showAllStudents, setShowAllStudents] = useState(false);
  const [allStudentsList, setAllStudentsList] = useState([]);
  const [unassignedStudentsList, setUnassignedStudentsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [collegeStats, setCollegeStats] = useState({});
  const [domainStats, setDomainStats] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [editFormData, setEditFormData] = useState({
    userName: '',
    userEmail: '',
    collegeName: '',
    areaOfInterest: '',
    currentlocation: '',
    userGender: '',
    countryPhoneCode: '',
    phoneNumber: '',
    reference: '',
    remarks: ''
  });

  const recordsPerPage = 10;

  // College List with their full names for API filtering
  const colleges = useMemo(() => [
    { id: 'pict', name: 'PICT College', shortName: 'PICT', filter: 'PICT', color: 'from-blue-500 to-blue-600' },
    { id: 'asian', name: 'Asian College', shortName: 'Asian', filter: 'Asian', color: 'from-green-500 to-green-600' },
    { id: 'trinity', name: 'Trinity/KJ College', shortName: 'Trinity', filter: 'Trinity', color: 'from-purple-500 to-purple-600' },
    { id: 'dbatu', name: 'DBATU University', shortName: 'DBATU', filter: 'DBATU', color: 'from-orange-500 to-orange-600' },
    { id: 'sinhgad', name: 'Sinhgad College Of Science', shortName: 'Sinhgad', filter: 'Sinhgad', color: 'from-pink-500 to-pink-600' },
    { id: 'sndt', name: 'SNDT College', shortName: 'SNDT', filter: 'SNDT', color: 'from-teal-500 to-teal-600' },
    { id: 'other', name: 'Another Colleges / Outside India', shortName: 'Other', filter: '', color: 'from-gray-500 to-gray-600' }
  ], []);

  // Domains List
  const domains = useMemo(() => [
    { id: 'fullstack', name: 'Full Stack Developer', icon: <FaCode />, color: 'bg-blue-500' },
    { id: 'aiml', name: 'AI/ML', icon: <FaBrain />, color: 'bg-purple-500' },
    { id: 'cloud', name: 'Cloud Computing', icon: <FaCloud />, color: 'bg-cyan-500' },
    { id: 'graphics', name: 'Graphics Designer', icon: <FaPaintBrush />, color: 'bg-pink-500' },
    { id: 'cybersecurity', name: 'Cybersecurity', icon: <FaShieldAlt />, color: 'bg-red-500' },
    { id: 'devops', name: 'DevOps', icon: <FaServer />, color: 'bg-green-500' }
  ], []);

  // Fetch all students on component mount
  useEffect(() => {
    fetchAllStudents();
  }, []);

  const fetchAllStudents = useCallback(async () => {
    setLoading(true);
    const startTime = performance.now();
    
    try {
      const data = await userMasterService.getAllUsers();
      const studentsList = Array.isArray(data) ? data : [];
      
      setAllStudentsList(studentsList);
      
      // Filter unassigned students (no college OR no domain)
      const unassigned = studentsList.filter(student => 
        !student.collegeName || !student.areaOfInterest || 
        student.collegeName === '' || student.areaOfInterest === ''
      );
      setUnassignedStudentsList(unassigned);
      
      calculateCollegeStats(studentsList);
      setDataLoaded(true);
      
      const endTime = performance.now();
      const loadTime = ((endTime - startTime) / 1000).toFixed(2);
      console.log(`Data loaded in ${loadTime} seconds`);
      
      if (parseFloat(loadTime) <= 2) {
        console.log('✓ Loading time meets requirement (≤2 seconds)');
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error(error?.response?.data?.error || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  }, []);

  // Calculate statistics for each college - optimized
  const calculateCollegeStats = useCallback((studentsList) => {
    const stats = {};
    const domainWiseStats = {};

    // Initialize stats objects
    colleges.forEach(college => {
      stats[college.id] = { total: 0, students: [] };
      domainWiseStats[college.id] = {};
      domains.forEach(domain => {
        domainWiseStats[college.id][domain.id] = { total: 0, students: [], name: domain.name };
      });
    });

    // Single pass through students for better performance
    studentsList.forEach(student => {
      const collegeName = student.collegeName || '';
      const areaOfInterest = (student.areaOfInterest || '').toLowerCase();
      
      // Find which college this student belongs to
      let studentCollege = null;
      for (const college of colleges) {
        if (college.id === 'other') {
          const isInOtherCollege = !colleges.some(c => c.id !== 'other' && collegeName.includes(c.filter));
          if (isInOtherCollege) {
            studentCollege = college;
            break;
          }
        } else if (collegeName.includes(college.filter)) {
          studentCollege = college;
          break;
        }
      }
      
      if (studentCollege) {
        stats[studentCollege.id].total++;
        stats[studentCollege.id].students.push(student);
        
        // Find which domain this student belongs to
        for (const domain of domains) {
          if (areaOfInterest.includes(domain.name.toLowerCase())) {
            domainWiseStats[studentCollege.id][domain.id].total++;
            domainWiseStats[studentCollege.id][domain.id].students.push(student);
            break;
          }
        }
      }
    });

    setCollegeStats(stats);
    setDomainStats(domainWiseStats);
  }, [colleges, domains]);

  // Export to Excel
  const exportToExcel = useCallback((data, filename) => {
    try {
      if (!data || data.length === 0) {
        toast.warning('No records to export');
        return;
      }

      const exportData = data.map(record => ({
        'Student ID': record.signUpId || '',
        'Name': record.userName || '',
        'Email': record.userEmail || '',
        'College': record.collegeName || 'N/A',
        'Area of Interest': record.areaOfInterest || 'N/A',
        'Location': record.currentlocation || 'N/A',
        'Gender': record.userGender || 'N/A',
        'Phone': `${record.countryPhoneCode || ''} ${record.phoneNumber || ''}`.trim(),
        'Reference': record.reference || 'N/A',
        'Remarks': record.remarks || 'N/A'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Students');
      XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  }, []);

  // Export to PDF
  const exportToPDF = useCallback((data, title, filename) => {
    try {
      if (!data || data.length === 0) {
        toast.warning('No records to export');
        return;
      }

      const doc = new jsPDF('landscape');
      
      doc.setFontSize(18);
      doc.setTextColor(41, 128, 185);
      doc.text(title, 14, 15);
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 25);
      doc.text(`Total Records: ${data.length}`, 14, 32);
      
      const tableData = data.map(record => [
        (record.signUpId || '').substring(0, 12),
        (record.userName || '').substring(0, 25),
        (record.userEmail || '').substring(0, 30),
        (record.collegeName || 'N/A').substring(0, 25),
        (record.areaOfInterest || 'N/A').substring(0, 20),
        record.currentlocation || 'N/A',
        record.userGender || 'N/A'
      ]);

      autoTable(doc, {
        head: [['ID', 'Name', 'Email', 'College', 'Domain', 'Location', 'Gender']],
        body: tableData,
        startY: 40,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 248, 255] }
      });

      doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF file downloaded successfully!');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF');
    }
  }, []);

  // Assign student to college and domain
  const assignStudentToCollege = useCallback(async (student, collegeId, domainId) => {
    setLoading(true);
    try {
      const college = colleges.find(c => c.id === collegeId);
      const domain = domains.find(d => d.id === domainId);
      
      if (!college || !domain) {
        toast.error('Invalid college or domain');
        return;
      }

      const updateData = {
        ...student,
        collegeName: college.name,
        areaOfInterest: domain.name
      };

      await userMasterService.updateUser(student.signUpId, updateData);
      toast.success(`✓ Student assigned to ${college.name} - ${domain.name}`);
      
      // Refresh the data after assignment
      await fetchAllStudents();
    } catch (error) {
      console.error('Error assigning student:', error);
      toast.error('Failed to assign student');
    } finally {
      setLoading(false);
    }
  }, [colleges, domains, fetchAllStudents]);

  // Show assign modal for unassigned students
  const handleAssignStudent = useCallback((student) => {
    Swal.fire({
      title: 'Assign Student',
      html: `
        <div class="text-left">
          <p class="mb-3 text-gray-600">Assigning: <strong>${student.userName}</strong></p>
          <select id="college-select" class="swal2-select" style="width: 100%; padding: 10px; margin-bottom: 15px; border-radius: 8px;">
            <option value="">-- Select College --</option>
            ${colleges.filter(c => c.id !== 'other').map(c => `<option value="${c.id}">${c.name}</option>`).join('')}
          </select>
          <select id="domain-select" class="swal2-select" style="width: 100%; padding: 10px; border-radius: 8px;">
            <option value="">-- Select Domain --</option>
            ${domains.map(d => `<option value="${d.id}">${d.name}</option>`).join('')}
          </select>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: '✓ Assign Student',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      preConfirm: () => {
        const collegeId = Swal.getPopup().querySelector('#college-select').value;
        const domainId = Swal.getPopup().querySelector('#domain-select').value;
        if (!collegeId || !domainId) {
          Swal.showValidationMessage('Please select both college and domain');
          return false;
        }
        return { collegeId, domainId };
      }
    }).then((result) => {
      if (result.isConfirmed) {
        assignStudentToCollege(student, result.value.collegeId, result.value.domainId);
      }
    });
  }, [colleges, domains, assignStudentToCollege]);

  // Navigation handlers
  const handleShowAllStudents = useCallback(() => {
    setShowAllStudents(true);
    setSelectedCollege(null);
    setSelectedDomain(null);
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  const handleCollegeClick = useCallback((college) => {
    setShowAllStudents(false);
    setSelectedCollege(college);
    setSelectedDomain(null);
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  const handleDomainClick = useCallback((domain) => {
    setSelectedDomain(domain);
    setCurrentPage(1);
  }, []);

  const handleBackToColleges = useCallback(() => {
    setShowAllStudents(false);
    setSelectedCollege(null);
    setSelectedDomain(null);
    setSearchTerm('');
  }, []);

  const handleBackToDomains = useCallback(() => {
    setSelectedDomain(null);
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  const handleBackFromAllStudents = useCallback(() => {
    setShowAllStudents(false);
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  const handleViewStudent = useCallback((student) => {
    setViewingStudent(student);
    setIsViewModalOpen(true);
  }, []);

  const handleEditStudent = useCallback((student) => {
    setEditingStudent(student);
    setEditFormData({
      userName: student.userName || '',
      userEmail: student.userEmail || '',
      collegeName: student.collegeName || '',
      areaOfInterest: student.areaOfInterest || '',
      currentlocation: student.currentlocation || '',
      userGender: student.userGender || '',
      countryPhoneCode: student.countryPhoneCode || '',
      phoneNumber: student.phoneNumber || '',
      reference: student.reference || '',
      remarks: student.remarks || ''
    });
    setIsEditModalOpen(true);
  }, []);

  const handleUpdateStudent = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const signUpId = editingStudent.signUpId;
      
      if (!signUpId) {
        toast.error('Invalid student ID');
        return;
      }
      
      await userMasterService.updateUser(signUpId, editFormData);
      toast.success('Student updated successfully');
      await fetchAllStudents();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating student:', error);
      const errorMessage = error?.response?.data?.error || 
                          error?.response?.data?.message || 
                          'Failed to update student';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [editingStudent, editFormData, fetchAllStudents]);

  const handleDeleteStudent = useCallback((student) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${student.userName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const signUpId = student.signUpId;
          if (!signUpId) {
            toast.error('Invalid student ID');
            return;
          }
          
          await userMasterService.deleteUser(signUpId);
          toast.success('Student deleted successfully');
          await fetchAllStudents();
        } catch (error) {
          console.error('Error deleting student:', error);
          toast.error(error?.response?.data?.error || 'Failed to delete student');
        } finally {
          setLoading(false);
        }
      }
    });
  }, [fetchAllStudents]);

  // Get filtered students based on current view - Memoized for performance
  const getFilteredStudents = useMemo(() => {
    let filtered = [];
    
    if (showAllStudents) {
      filtered = [...unassignedStudentsList];
    } else if (selectedCollege && selectedDomain) {
      const domainData = domainStats[selectedCollege?.id]?.[selectedDomain?.id];
      filtered = domainData?.students || [];
    } else if (selectedCollege && !selectedDomain) {
      // Don't show students at domain level, just counts
      return [];
    }
    
    // Apply search filter
    if (searchTerm && filtered.length > 0) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(student =>
        (student.userName?.toLowerCase() || '').includes(searchLower) ||
        (student.userEmail?.toLowerCase() || '').includes(searchLower) ||
        (student.collegeName?.toLowerCase() || '').includes(searchLower) ||
        (student.areaOfInterest?.toLowerCase() || '').includes(searchLower)
      );
    }
    
    return filtered;
  }, [showAllStudents, unassignedStudentsList, selectedCollege, selectedDomain, domainStats, searchTerm]);

  // Pagination
  const totalPages = Math.ceil((getFilteredStudents.length || 0) / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentStudents = getFilteredStudents.slice(startIndex, endIndex);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showAllStudents, selectedCollege, selectedDomain]);

  // Get counts
  const getUnassignedCount = useCallback(() => unassignedStudentsList.length, [unassignedStudentsList]);
  const getAssignedCount = useCallback(() => allStudentsList.length - unassignedStudentsList.length, [allStudentsList, unassignedStudentsList]);

  // Render Colleges View
  const renderCollegesView = useCallback(() => {
    const totalStudents = allStudentsList.length;
    const unassignedCount = getUnassignedCount();
    const assignedCount = getAssignedCount();
    
    return (
      <div>
        <div className="mb-8">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                🎓 User Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage students by college and domain
              </p>
            </div>
            <div className="flex gap-3">
              <div className="bg-green-100 dark:bg-green-900 rounded-lg px-4 py-2 text-center">
                <p className="text-xs text-green-600 dark:text-green-400">Assigned</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-300">{assignedCount}</p>
              </div>
              <div className="bg-yellow-100 dark:bg-yellow-900 rounded-lg px-4 py-2 text-center">
                <p className="text-xs text-yellow-600 dark:text-yellow-400">Need Assignment</p>
                <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{unassignedCount}</p>
              </div>
              <button
                onClick={() => exportToExcel(allStudentsList, 'all_students')}
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
              >
                <FaFileExcel /> Export All Excel
              </button>
              <button
                onClick={() => exportToPDF(allStudentsList, 'All Students Report', 'all_students')}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
              >
                <FaFilePdf /> Export All PDF
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* SORT STUDENTS BOX - Only shows unassigned students */}
          <div
            className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer overflow-hidden group"
            onClick={handleShowAllStudents}
          >
            <div className="p-4 bg-white bg-opacity-10">
              <div className="flex justify-between items-center">
                <FaSortAmountDown className="text-white text-3xl" />
                <div className="bg-white bg-opacity-20 rounded-full px-3 py-1">
                  <span className="text-white font-bold text-lg">{unassignedCount}</span>
                  <span className="text-white text-sm ml-1">Need Sort</span>
                </div>
              </div>
            </div>
            <div className="p-6 text-white">
              <h3 className="text-xl font-bold mb-2">📋 Sort Students</h3>
              <p className="text-white text-opacity-90 mb-4">
                Students that need to be assigned to a college and domain
              </p>
              {unassignedCount > 0 ? (
                <div className="mt-2 text-sm bg-white bg-opacity-20 rounded p-2 text-center">
                  ⚠️ {unassignedCount} student{unassignedCount !== 1 ? 's' : ''} need assignment
                </div>
              ) : (
                <div className="mt-2 text-sm bg-green-500 bg-opacity-30 rounded p-2 text-center flex items-center justify-center gap-2">
                  <FaCheckCircle /> All students assigned!
                </div>
              )}
            </div>
          </div>

          {/* College Boxes */}
          {colleges.map((college) => {
            const stats = collegeStats[college.id] || { total: 0 };
            return (
              <div
                key={college.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border border-gray-200 dark:border-gray-700 overflow-hidden group"
                onClick={() => handleCollegeClick(college)}
              >
                <div className={`bg-gradient-to-r ${college.color} p-4`}>
                  <div className="flex justify-between items-center">
                    <FaUniversity className="text-white text-3xl" />
                    <div className="bg-white bg-opacity-20 rounded-full px-3 py-1">
                      <span className="text-white font-bold text-lg">{stats.total}</span>
                      <span className="text-white text-sm ml-1">Students</span>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {college.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Total {stats.total} students enrolled
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {domains.map(domain => {
                      const domainCount = domainStats[college.id]?.[domain.id]?.total || 0;
                      if (domainCount > 0) {
                        return (
                          <span key={domain.id} className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {domain.name}: {domainCount}
                          </span>
                        );
                      }
                      return null;
                    })}
                    {stats.total === 0 && (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500">
                        No students yet
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [allStudentsList, colleges, collegeStats, domainStats, domains, exportToExcel, exportToPDF, getUnassignedCount, getAssignedCount, handleCollegeClick, handleShowAllStudents]);

  // Render Sort Students View
  const renderAllStudentsView = useCallback(() => {
    const unassignedCount = getUnassignedCount();
    
    return (
      <div>
        <div className="mb-8">
          <button
            onClick={handleBackFromAllStudents}
            className="mb-4 flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-colors"
          >
            <FaArrowLeft className="mr-2" /> Back to Colleges
          </button>
          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg p-6 text-white">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-bold mb-2">📋 Sort Students</h1>
                <p className="text-lg opacity-90">
                  Students that need to be assigned to a college and domain
                </p>
                <p className="text-sm mt-2">
                  Total Pending: {getFilteredStudents.length} student{getFilteredStudents.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => exportToExcel(getFilteredStudents, 'unassigned_students')}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
                >
                  <FaFileExcel /> Export Excel
                </button>
                <button
                  onClick={() => exportToPDF(getFilteredStudents, 'Unassigned Students Report', 'unassigned_students')}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
                >
                  <FaFilePdf /> Export PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        {unassignedCount === 0 ? (
          <div className="bg-green-100 dark:bg-green-900 rounded-lg p-8 text-center">
            <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-green-700 dark:text-green-300 mb-2">All Students Assigned!</h3>
            <p className="text-gray-600 dark:text-gray-400">All students have been assigned to a college and domain.</p>
            <button
              onClick={handleBackFromAllStudents}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors"
            >
              Back to Colleges
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="relative">
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search students by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Current College</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Current Domain</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {currentStudents.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">No unassigned students found</td>
                      </tr>
                    ) : (
                      currentStudents.map((student) => (
                        <tr key={student.signUpId} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors bg-yellow-50 dark:bg-yellow-900/10">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{student.userName || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{student.userEmail || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm">
                            {student.collegeName ? (
                              <span className="text-gray-600 dark:text-gray-300">{student.collegeName}</span>
                            ) : (
                              <span className="text-yellow-500 font-medium">Not Assigned</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {student.areaOfInterest ? (
                              <span className="text-gray-600 dark:text-gray-300">{student.areaOfInterest}</span>
                            ) : (
                              <span className="text-yellow-500 font-medium">Not Assigned</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                            <button onClick={() => handleViewStudent(student)} className="text-green-600 hover:text-green-900 mr-2 transition-colors" title="View">
                              <FaEye />
                            </button>
                            <button onClick={() => handleEditStudent(student)} className="text-blue-600 hover:text-blue-900 mr-2 transition-colors" title="Edit">
                              <FaEdit />
                            </button>
                            <button onClick={() => handleAssignStudent(student)} className="text-purple-600 hover:text-purple-900 mr-2 transition-colors" title="Assign to College & Domain">
                              <FaPlus />
                            </button>
                            <button onClick={() => handleDeleteStudent(student)} className="text-red-600 hover:text-red-900 transition-colors" title="Delete">
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 flex-wrap gap-4">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {startIndex + 1} to {Math.min(endIndex, getFilteredStudents.length)} of {getFilteredStudents.length} students
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                    disabled={currentPage === 1} 
                    className="px-3 py-2 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <FaChevronLeft className="inline mr-1" /> Previous
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                    disabled={currentPage === totalPages} 
                    className="px-3 py-2 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    Next <FaChevronRight className="inline ml-1" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }, [getFilteredStudents, getUnassignedCount, handleBackFromAllStudents, searchTerm, currentStudents, totalPages, startIndex, endIndex, currentPage, handleViewStudent, handleEditStudent, handleAssignStudent, handleDeleteStudent, exportToExcel, exportToPDF]);

  // Render Domains View
  const renderDomainsView = useCallback(() => {
    const collegeData = collegeStats[selectedCollege?.id];
    const totalStudents = collegeData?.total || 0;
    
    return (
      <div>
        <div className="mb-8">
          <button
            onClick={handleBackToColleges}
            className="mb-4 flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-colors"
          >
            <FaArrowLeft className="mr-2" /> Back to Colleges
          </button>
          <div className={`bg-gradient-to-r ${selectedCollege?.color} rounded-lg p-6 text-white`}>
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-bold mb-2">{selectedCollege?.name}</h1>
                <p className="text-lg opacity-90">Total Students: {totalStudents}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => exportToExcel(collegeData?.students || [], selectedCollege?.name.replace(/\s/g, '_'))}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
                >
                  <FaFileExcel /> Export Excel
                </button>
                <button
                  onClick={() => exportToPDF(collegeData?.students || [], `${selectedCollege?.name} Report`, selectedCollege?.name.replace(/\s/g, '_'))}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
                >
                  <FaFilePdf /> Export PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {domains.map((domain) => {
            const domainData = domainStats[selectedCollege?.id]?.[domain.id];
            const count = domainData?.total || 0;
            
            return (
              <div
                key={domain.id}
                className={`bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border border-gray-200 dark:border-gray-700 overflow-hidden ${count === 0 ? 'opacity-60' : ''}`}
                onClick={() => count > 0 && handleDomainClick(domain)}
              >
                <div className={`${domain.color} p-4 text-white`}>
                  <div className="flex justify-between items-center">
                    <div className="text-3xl">{domain.icon}</div>
                    <div className="bg-white bg-opacity-20 rounded-full px-3 py-1">
                      <span className="font-bold text-lg">{count}</span>
                      <span className="text-sm ml-1">Students</span>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{domain.name}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{count} students specialized in {domain.name}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [selectedCollege, collegeStats, domainStats, domains, exportToExcel, exportToPDF, handleBackToColleges, handleDomainClick]);

  // Render Students List View
  const renderStudentsView = useCallback(() => {
    return (
      <div>
        <div className="mb-8">
          <button
            onClick={handleBackToDomains}
            className="mb-4 flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-colors"
          >
            <FaArrowLeft className="mr-2" /> Back to Domains
          </button>
          <div className={`bg-gradient-to-r ${selectedCollege?.color} rounded-lg p-6 text-white`}>
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-bold mb-2">{selectedCollege?.name}</h1>
                <p className="text-xl mb-2">{selectedDomain?.name}</p>
                <p className="text-lg opacity-90">Total Students: {getFilteredStudents.length}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => exportToExcel(getFilteredStudents, `${selectedCollege?.name}_${selectedDomain?.name}`.replace(/\s/g, '_'))}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
                >
                  <FaFileExcel /> Export Excel
                </button>
                <button
                  onClick={() => exportToPDF(getFilteredStudents, `${selectedCollege?.name} - ${selectedDomain?.name} Report`, `${selectedCollege?.name}_${selectedDomain?.name}`.replace(/\s/g, '_'))}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
                >
                  <FaFilePdf /> Export PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">College</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Domain</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {currentStudents.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">No students found</td>
                  </tr>
                ) : (
                  currentStudents.map((student) => (
                    <tr key={student.signUpId} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{student.userName || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{student.userEmail || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{student.collegeName || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{student.areaOfInterest || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                        <button onClick={() => handleViewStudent(student)} className="text-green-600 hover:text-green-900 mr-2 transition-colors" title="View">
                          <FaEye />
                        </button>
                        <button onClick={() => handleEditStudent(student)} className="text-blue-600 hover:text-blue-900 mr-2 transition-colors" title="Edit">
                          <FaEdit />
                        </button>
                        <button onClick={() => handleDeleteStudent(student)} className="text-red-600 hover:text-red-900 transition-colors" title="Delete">
                          <FaTrash />
                        </button>
                       </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 flex-wrap gap-4">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {startIndex + 1} to {Math.min(endIndex, getFilteredStudents.length)} of {getFilteredStudents.length} students
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                disabled={currentPage === 1} 
                className="px-3 py-2 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                <FaChevronLeft className="inline mr-1" /> Previous
              </button>
              <span className="px-3 py-2 text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <button 
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                disabled={currentPage === totalPages} 
                className="px-3 py-2 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Next <FaChevronRight className="inline ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }, [selectedCollege, selectedDomain, getFilteredStudents, searchTerm, currentStudents, totalPages, startIndex, endIndex, currentPage, handleBackToDomains, exportToExcel, exportToPDF, handleViewStudent, handleEditStudent, handleDeleteStudent]);

  // Modals
  const renderViewModal = useCallback(() => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setIsViewModalOpen(false)}>
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Student Details</h2>
          <button onClick={() => setIsViewModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl">✕</button>
        </div>
        {viewingStudent && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Student ID</label>
                <p className="text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md text-gray-900 dark:text-white">{viewingStudent.signUpId || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <p className="text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md text-gray-900 dark:text-white">{viewingStudent.userName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <p className="text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md text-gray-900 dark:text-white">{viewingStudent.userEmail || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">College</label>
                <p className="text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md text-gray-900 dark:text-white">{viewingStudent.collegeName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Domain</label>
                <p className="text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md text-gray-900 dark:text-white">{viewingStudent.areaOfInterest || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                <p className="text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md text-gray-900 dark:text-white">
                  {viewingStudent.countryPhoneCode} {viewingStudent.phoneNumber}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
                <p className="text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md text-gray-900 dark:text-white">{viewingStudent.currentlocation || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gender</label>
                <p className="text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md text-gray-900 dark:text-white">{viewingStudent.userGender || 'N/A'}</p>
              </div>
            </div>
            {viewingStudent.reference && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reference</label>
                <p className="text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md text-gray-900 dark:text-white">{viewingStudent.reference}</p>
              </div>
            )}
            {viewingStudent.remarks && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Remarks</label>
                <p className="text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md text-gray-900 dark:text-white">{viewingStudent.remarks}</p>
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end mt-6">
          <button onClick={() => setIsViewModalOpen(false)} className="px-4 py-2 text-sm text-white bg-gray-600 rounded-md hover:bg-gray-700 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  ), [viewingStudent]);

  const renderEditModal = useCallback(() => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setIsEditModalOpen(false)}>
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Student</h2>
          <button onClick={() => setIsEditModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl">✕</button>
        </div>
        <form onSubmit={handleUpdateStudent}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name *</label>
              <input 
                type="text" 
                value={editFormData.userName} 
                onChange={(e) => setEditFormData({...editFormData, userName: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" 
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email *</label>
              <input 
                type="email" 
                value={editFormData.userEmail} 
                onChange={(e) => setEditFormData({...editFormData, userEmail: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" 
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">College</label>
              <select 
                value={editFormData.collegeName} 
                onChange={(e) => setEditFormData({...editFormData, collegeName: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select College</option>
                {colleges.filter(c => c.id !== 'other').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Domain</label>
              <select 
                value={editFormData.areaOfInterest} 
                onChange={(e) => setEditFormData({...editFormData, areaOfInterest: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select Domain</option>
                {domains.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
              <input 
                type="text" 
                value={editFormData.currentlocation} 
                onChange={(e) => setEditFormData({...editFormData, currentlocation: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gender</label>
              <select 
                value={editFormData.userGender} 
                onChange={(e) => setEditFormData({...editFormData, userGender: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Code</label>
              <input 
                type="text" 
                value={editFormData.countryPhoneCode} 
                onChange={(e) => setEditFormData({...editFormData, countryPhoneCode: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" 
                placeholder="+91"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
              <input 
                type="text" 
                value={editFormData.phoneNumber} 
                onChange={(e) => setEditFormData({...editFormData, phoneNumber: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Reference</label>
              <input 
                type="text" 
                value={editFormData.reference} 
                onChange={(e) => setEditFormData({...editFormData, reference: e.target.value})} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Remarks</label>
              <textarea 
                value={editFormData.remarks} 
                onChange={(e) => setEditFormData({...editFormData, remarks: e.target.value})} 
                rows="3" 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <button 
              type="button" 
              onClick={() => setIsEditModalOpen(false)} 
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading} 
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Updating...' : 'Update Student'}
            </button>
          </div>
        </form>
      </div>
    </div>
  ), [editFormData, loading, handleUpdateStudent, colleges, domains]);

  // Loading state
  if (loading && !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FaSpinner className="text-4xl text-blue-600 animate-spin mx-auto mb-4" />
          <span className="text-lg text-gray-600 dark:text-gray-400">Loading data...</span>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="p-6 min-h-screen bg-gray-50 dark:bg-gray-900">
      {!selectedCollege && !showAllStudents && renderCollegesView()}
      {showAllStudents && renderAllStudentsView()}
      {selectedCollege && !selectedDomain && renderDomainsView()}
      {selectedCollege && selectedDomain && renderStudentsView()}
      {isViewModalOpen && renderViewModal()}
      {isEditModalOpen && renderEditModal()}
    </div>
  );
};

export default UserMaster;